import axios from 'axios'
import { ElMessage } from 'element-plus'
import { getToken, logout } from '@/utils/auth'

const HTTP_STATUS = {
  OK: 200,            // 请求成功
  BAD_REQUEST: 400,   // 客户端请求错误
  UNAUTHORIZED: 401,  // 未授权
  FORBIDDEN: 403,     // 禁止访问
  NOT_FOUND: 404,     // 资源不存在
  SERVER_ERROR: 500   // 服务器错误
}

export const ERROR_TYPE = {
  NETWORK: 'NETWORK_ERROR',    // 网络错误
  TIMEOUT: 'TIMEOUT_ERROR',    // 请求超时
  LOGIN_ERROR: 'LOGIN_ERROR',  // 登录错误
  NO_TOKEN: 'NO_TOKEN_ERROR',  // 无令牌
  TOKEN_EXPIRED: 'TOKEN_EXPIRED', // 令牌过期
  TOKEN_INVALID: 'TOKEN_INVALID', // 令牌无效
  AUTH_ERROR: 'AUTH_ERROR',    // 认证错误
  UNKNOWN: 'UNKNOWN_ERROR'     // 未知错误
}

const ERROR_CODE = {
  TIMEOUT: 'ECONNABORTED',      // axios 超时错误代码
  NETWORK: 'ERR_NETWORK',       // axios 网络错误代码
  TOKEN_EXPIRED: 'TOKEN_EXPIRED', // 后端返回的令牌过期代码
  TOKEN_INVALID: 'TOKEN_INVALID'  // 后端返回的令牌无效代码
}

const ERROR_MESSAGES = {
  [HTTP_STATUS.BAD_REQUEST]: '请求参数错误',
  [HTTP_STATUS.UNAUTHORIZED]: '未授权或登录已过期',
  [HTTP_STATUS.FORBIDDEN]: '拒绝访问',
  [HTTP_STATUS.NOT_FOUND]: '请求的资源不存在',
  [HTTP_STATUS.SERVER_ERROR]: '服务器内部错误',
  [ERROR_TYPE.NETWORK]: '网络连接失败',
  [ERROR_TYPE.TIMEOUT]: '请求超时',
  'default': '请求失败'
}

const { VUE_APP_BASE_API } = process.env
if (!VUE_APP_BASE_API) {
  throw new Error('VUE_APP_BASE_API 未定义，请在 .env 或 .env.development 中配置')
}

const service = axios.create({
  baseURL: VUE_APP_BASE_API,
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json'
  }
})

service.interceptors.request.use(
  config => {
    const token = getToken()

    if (token) {
      config.headers['authorization'] = token
    }
    return config
  },
  error => {
    console.error('[Request Error]', error)
    return Promise.reject(error)
  }
)

function businessMessage (res) {
  if (res && typeof res === 'object' && res.message) {
    return String(res.message)
  }
  return ERROR_MESSAGES.default
}

function isBusinessFail (res) {
  if (res == null || typeof res !== 'object') {
    return false
  }
  if (res.ok === false || res.isOk === false) {
    return true
  }
  return typeof res.code === 'number' && res.code !== HTTP_STATUS.OK
}

export function requestErrorMessage (error, fallback = ERROR_MESSAGES.default) {
  if (error && error.data && error.data.message) {
    return String(error.data.message)
  }
  if (error && error.message) {
    return String(error.message)
  }
  return fallback
}

function rejectWithToast (message, extra = {}) {
  const text = (message && String(message).trim()) || ERROR_MESSAGES.default
  ElMessage.error(text)
  const err = new Error(text)
  Object.assign(err, extra, { message: text, _notified: true })
  return Promise.reject(err)
}

service.interceptors.response.use(
  response => {
    const res = response.data
    if (isBusinessFail(res)) {
      return rejectWithToast(businessMessage(res), { data: res, status: response.status })
    }
    return res
  },
  error => {
    const err = {
      status: error.response?.status,
      data: error.response?.data,
      code: error.code,
      message: '',
      type: ERROR_TYPE.UNKNOWN
    }

    if (error.response) {
      const { status, data } = error.response
      err.message = data?.message || ERROR_MESSAGES[status] || ERROR_MESSAGES.default

      if (status === HTTP_STATUS.UNAUTHORIZED) {
        handleAuthError(error, data, err);
      }
    } else {
      handleNonResponseError(error, err);
    }

    return rejectWithToast(err.message, err)
  }
)

function handleAuthError () {
  logout({ showMessage: false });
}

function handleNonResponseError (error, err) {
  const errorTypes = {
    [ERROR_CODE.TIMEOUT]: {
      type: ERROR_TYPE.TIMEOUT,
      message: ERROR_MESSAGES[ERROR_TYPE.TIMEOUT]
    },
    [ERROR_CODE.NETWORK]: {
      type: ERROR_TYPE.NETWORK,
      message: ERROR_MESSAGES[ERROR_TYPE.NETWORK]
    }
  };

  const errorInfo = errorTypes[error.code] || {
    type: ERROR_TYPE.UNKNOWN,
    message: error.message || ERROR_MESSAGES.default
  };

  err.type = errorInfo.type;
  err.message = errorInfo.message;
}

export default service
